"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Milestones() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const milestones = [{
    year: "2018",
    title: "Founding Vision",
    description: "Price Advocate Services was established to level the playing field for consumers. We began by helping families navigate complex automotive and home appliance negotiations."
  }, {
    year: "2020",
    title: "Digital Expansion",
    description: "We launched our online consultation platform, allowing us to assist clients nationwide in securing fair pricing for high-ticket purchases from the comfort of their homes."
  }, {
    year: "2022",
    title: "The 1,000th Win",
    description: "Celebrating our thousandth successful negotiation. This milestone solidified our reputation as a trusted partner in consumer advocacy and financial protection."
  }, {
    year: "2023",
    title: "Strategic Partnerships",
    description: "We formed alliances with independent industry experts to provide deeper insights into market trends, ensuring our clients always have the upper hand in every deal."
  }, {
    year: "2024",
    title: "Advanced Advocacy",
    description: "Introduction of our premium negotiation suite, featuring real-time market data analysis and dedicated advocate support for complex, multi-stage purchase agreements."
  }, {
    year: "2025",
    title: "Future of Fairness",
    description: "Today, we continue to redefine the consumer experience. With thousands of successful outcomes, we remain committed to transparency, integrity, and your financial well-being."
  }];
  return <section data-section-id="3312" id="success-milestones" className="bg-white text-[#333333] py-32 md:py-[200px] px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`flex items-center gap-3 mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="block w-8 h-px bg-[#1a3a6b]" />
          <span className="font-sans text-[11px] uppercase tracking-[0.2em] text-[#1a3a6b] font-bold">
            03 — Our Journey
          </span>
        </div>

        {}
        <div className={`text-center mb-24 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-default font-bold text-[#1a3a6b] text-5xl md:text-7xl lg:text-[80px] leading-[0.95] tracking-tighter uppercase">
            <span className="block">Years of</span>
            <span className="block md:translate-x-12 lg:translate-x-20 mt-2">
              Advocacy.
            </span>
          </h2>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute top-0 bottom-0 w-px bg-[#cccccc]/50 left-4 md:left-1/2 md:-translate-x-1/2" aria-hidden="true" />

          <div className="flex flex-col">
            {milestones.map((milestone, i) => {
            const isLeft = i % 2 === 0;
            const isFirst = i === 0;
            const isLast = i === milestones.length - 1;
            return <MilestoneEntry key={i} data-index={i} milestone={milestone} isLeft={isLeft} isFirst={isFirst} isLast={isLast} />;
          })}
          </div>
        </div>
      </div>
    </section>;
}
type MilestoneEntryProps = {
  milestone: {
    year: string;
    title: string;
    description: string;
  };
  isLeft: boolean;
  isFirst: boolean;
  isLast: boolean;
  "data-index"?: number;
};
function MilestoneEntry({
  milestone,
  isLeft,
  isFirst,
  isLast,
  ...rest
}: MilestoneEntryProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const dotSize = isFirst ? "w-4 h-4" : "w-2.5 h-2.5";
  return <div ref={ref} {...rest} className={`relative min-h-[160px] md:min-h-[180px] py-6 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      {}
      <div className={`absolute top-8 left-4 md:left-1/2 -translate-x-1/2 ${dotSize} bg-[#e87a27] z-10 rounded-full ${isLast ? "animate-pulse shadow-lg" : ""}`} aria-hidden="true" />

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-16">
        {}
        <div className={`hidden md:flex flex-col ${isLeft ? "md:items-end md:text-right md:pr-12" : "md:invisible"}`}>
          {isLeft && <>
              <div className="font-default font-bold tabular-nums text-[#1a3a6b] text-4xl lg:text-5xl tracking-tight mb-3">
                {milestone.year}
              </div>
              <h3 className="font-default font-bold text-[#1a3a6b] text-[22px] leading-tight mb-4 uppercase">
                {milestone.title}
              </h3>
              <p className="font-sans text-[15px] leading-relaxed text-[#333333]/80 max-w-[280px]">
                {milestone.description}
              </p>
            </>}
        </div>

        {}
        <div className={`hidden md:flex flex-col ${!isLeft ? "md:items-start md:text-left md:pl-12" : "md:invisible"}`}>
          {!isLeft && <>
              <div className="font-default font-bold tabular-nums text-[#1a3a6b] text-4xl lg:text-5xl tracking-tight mb-3">
                {milestone.year}
              </div>
              <h3 className="font-default font-bold text-[#1a3a6b] text-[22px] leading-tight mb-4 uppercase">
                {milestone.title}
              </h3>
              <p className="font-sans text-[15px] leading-relaxed text-[#333333]/80 max-w-[280px]">
                {milestone.description}
              </p>
            </>}
        </div>

        {}
        <div className="md:hidden pl-12 flex flex-col">
          <div className="font-default font-bold tabular-nums text-[#1a3a6b] text-3xl tracking-tight mb-2">
            {milestone.year}
          </div>
          <h3 className="font-default font-bold text-[#1a3a6b] text-lg leading-tight mb-3 uppercase">
            {milestone.title}
          </h3>
          <p className="font-sans text-[15px] leading-relaxed text-[#333333]/80 max-w-[280px]">
            {milestone.description}
          </p>
        </div>
      </div>
    </div>;
}